import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { TableSearchField } from './TableSearchField';
import { PolicyTypeFilter } from './Filters/PolicyTypeFilter';
import { ComplianceStatusFilter } from './Filters/ComplianceStatusFilter';
import { RiListApplication } from '../../../types';
import { DateFormat, time } from '@/shared/libs/time.lib';
import { json2csv } from 'json-2-csv';
import { ExportButtonDropdown } from './ExportButtonDropdown';
import { useToast } from '@/components/Toast';
import {
  getReasonsNotCompliantMessage,
  mapRiApplicationSourceToDisplayedValue,
} from '@/features/RentersInsurance/utils';
import { PropertyFilter } from './Filters/PropertyFilter';

const ContainerStyled = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  backgroundColor: '#080A2D0A',
  borderBottom: `1px solid ${theme.palette.border.secondary}`,
}));

type Props = {
  rowsToExport: RiListApplication[];
  isDataLoading: boolean;
};

const parseToCsvData = (row: RiListApplication) => {
  const {
    address,
    firstName,
    buildingId,
    complianceStatus,
    email,
    lastName,
    source,
    reasonsNotCompliant,
    updatedAt,
    buildingUnit,
  } = row;

  return {
    'Building name': address?.addressName,
    Unit: buildingUnit,
    Email: email,
    'First name': firstName,
    'Last name': lastName,
    'Policy type': mapRiApplicationSourceToDisplayedValue(source),
    'Building ID': buildingId,
    'Last updated': updatedAt,
    Status: complianceStatus?.toString(),
    'Not compliant reasons': getReasonsNotCompliantMessage(reasonsNotCompliant).join(', '),
  };
};

export function RiApplicationTableHeader({ rowsToExport, isDataLoading }: Props) {
  const { success } = useToast();

  const isExportButtonDisabled = rowsToExport.length === 0 || isDataLoading;

  const handleRowsExport = () => {
    const numberOfRows = rowsToExport.length;

    const jsonData = rowsToExport.map((row) => parseToCsvData(row));
    const csvContent = json2csv(jsonData, {});

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Renters-insurance-applications-${time().format(DateFormat.Calendar)}`;
    link.click();

    success(`${numberOfRows} ${numberOfRows === 1 ? 'item' : 'items'} successfully exported`);
  };

  return (
    <ContainerStyled data-testid="ri-applications-table-header">
      <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
        <Stack flexDirection="row" alignItems="center">
          <TableSearchField label="Search by renter name or email" />

          <Stack flexDirection="row" alignItems="center" sx={{ mx: 2, gap: 2 }}>
            <PropertyFilter />
            <ComplianceStatusFilter />
            <PolicyTypeFilter />
          </Stack>
        </Stack>

        <ExportButtonDropdown
          isExportButtonDisabled={isExportButtonDisabled}
          onExport={handleRowsExport}
        />
      </Stack>
    </ContainerStyled>
  );
}
